/**
 * RecentActivityList - Shows recent signups with relative timestamps.
 */

import { Link } from "react-router-dom";

interface RecentSignup {
  id: string;
  email: string;
  createdAt: string;
}

interface RecentActivityListProps {
  signups: RecentSignup[];
  loading?: boolean;
  title?: string;
}

/**
 * Format a date as a relative time string (e.g., "2m ago", "1h ago")
 */
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return "just now";
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function RecentActivityList({
  signups,
  loading = false,
  title = "Recent Signups",
}: RecentActivityListProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 w-32 bg-gray-200 rounded mb-4 animate-pulse" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-gray-200" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 rounded" />
                <div className="h-3 w-16 bg-gray-100 rounded mt-1" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {signups.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-8">
          No recent signups
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {signups.map((signup) => (
              <Link
                key={signup.id}
                to={`/admin/users/${signup.id}`}
                className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                {/* Avatar placeholder */}
                <div className="w-8 h-8 rounded-full bg-magic-100 text-magic-600 flex items-center justify-center text-sm font-medium">
                  {signup.email.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate group-hover:text-magic-600 transition-colors">
                    {signup.email}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatRelativeTime(signup.createdAt)}
                  </p>
                </div>
                <ChevronRightIcon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </Link>
            ))}
          </div>
          <Link
            to="/admin/users"
            className="mt-4 flex items-center justify-center gap-1 text-sm font-medium text-magic-600 hover:text-magic-700 transition-colors"
          >
            View all
            <ChevronRightIcon className="w-4 h-4" />
          </Link>
        </>
      )}
    </div>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default RecentActivityList;
